// Central schema + shape helpers for the Create/Edit Order modal.
// Adding a new specification field: put it in INITIAL_ORDER_DATA plus the
// relevant flatten/build helper. No need to touch the modal container.

// -------- 1) Initial form state --------
export const INITIAL_ORDER_DATA = {
  // Cylinder / branding
  manufactured_by: '', marketed_by: '', brand_name: '', product_name: '', qty: '', packed_date: '', bar_code: '',
  // Job & dates
  job_name: '', design_approved_date: '', separations_approved_date: '', delivery_date: '', pricing: '', notes: '',
  // Cylinder specs
  width: '', height: '', ups_in_circum: '', ups_in_length: '', circumference: '', length: '',
  nos_of_cylinders: '', bore: '', degree: '', trimming: '', extra_length: '20', actual_extra_length: '',
  // Prepress / planning (Yes/No fields)
  pcb: '', veg_logo: '', white: '', overcoat: '', mattcoat: '', barcode_genuine: '', barcode_fake: '',
  repeat_trap: '', registration_marks: '', slitting: '', arc_marks: '', gray_scale_circles: '', tt_line: '',
  special_colour_patches: '', coil_no: '', photocell_size: '', photocell_colour: '',
  key_way: '', new_baseshell: '', pbs: '', biller: '',
  registration_marks_file: null, registration_marks_url: '', registration_marks_position: '', planning_special_instructions: '',
  // Engraving
  engraving_type: '', double_engraving: '', screen: '', gsm: '', engraving_special_instructions: '', rework_history: '',
  // Proofing
  printing_substrate: '', lamination_with: '', image_to_match_with: '', special_colours_to_match_with: '',
  special_colour_ink_provided: '', proofing_to_be_laminated: '', colours: [''], proof_matching_reference: '',
  proofing_instruction: '', proofing_remark: '',
  // Billing / dispatch
  consignee: '', transporter: '', dispatch_date: '',
};

// -------- 2) Order → Form (for edit flow) --------
const boolToYesNo = (val) => (val ? 'Yes' : (val === false ? 'No' : ''));

// <input type="date"> only accepts "YYYY-MM-DD". Backend returns full ISO
// strings ("2026-09-15T00:00:00"), which the browser silently ignores,
// leaving the field empty and causing round-trip loss on save.
const toDateInput = (val) => {
  if (!val) return '';
  const s = String(val);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '';
};

export const flattenOrderToForm = (order) => {
  const planning = order.planning_spec || order.planning_specification || {};
  const engraving = order.engraving_spec || order.engraving_specification || {};
  const proofing = order.proofing_spec || order.proofing_specification || {};
  const specs = order.specifications || {};
  const cylinder = order.cylinder_details || {};
  const billing = order.billing_dispatch_details || {};

  return {
    manufactured_by: cylinder.manufactured_by || specs.manufactured_by || '',
    marketed_by: cylinder.marketed_by || specs.marketed_by || '',
    brand_name: cylinder.brand_name || specs.brand_name || '',
    product_name: cylinder.product_name || specs.product_name || '',
    qty: cylinder.qty || specs.qty || '',
    packed_date: toDateInput(cylinder.packed_date || specs.packed_date),
    bar_code: cylinder.bar_code || specs.bar_code || '',
    job_name: order.job_name || '',
    design_approved_date: toDateInput(planning.design_approved_date || order.design_approved_date),
    separations_approved_date: toDateInput(planning.separations_approved_date || order.separations_approved_date),
    delivery_date: toDateInput(order.delivery_date),
    pricing: billing.pricing || order.pricing || '',
    notes: order.notes || '',
    width: specs.width?.toString() || '', height: specs.height?.toString() || '',
    ups_in_length: specs.ups_in_length?.toString() || '', ups_in_circum: specs.ups_in_circum?.toString() || '',
    circumference: specs.circumference?.toString() || '', length: specs.length?.toString() || '',
    nos_of_cylinders: specs.nos_of_cylinders?.toString() || '',
    bore: specs.bore?.toString() || '', degree: specs.degree?.toString() || '',
    trimming: specs.trimming?.toString() || '', extra_length: specs.extra_length?.toString() || '20',
    actual_extra_length: specs.actual_extra_length?.toString() || '',
    key_way: specs.key_way?.toString() || '', new_baseshell: specs.new_baseshell || '', pbs: specs.pbs || '',
    biller: order.biller || '',
    pcb: boolToYesNo(planning.pcb), veg_logo: boolToYesNo(planning.veg_logo), white: boolToYesNo(planning.white),
    overcoat: boolToYesNo(planning.overcoat), mattcoat: boolToYesNo(planning.mattcoat),
    barcode_genuine: boolToYesNo(planning.barcode_genuine), barcode_fake: boolToYesNo(planning.barcode_fake),
    repeat_trap: boolToYesNo(planning.repeat_trap), registration_marks: boolToYesNo(planning.registration_marks),
    slitting: boolToYesNo(planning.slitting), arc_marks: boolToYesNo(planning.arc_marks),
    gray_scale_circles: boolToYesNo(planning.gray_scale_circles), tt_line: boolToYesNo(planning.tt_line),
    special_colour_patches: boolToYesNo(planning.special_colour_patches),
    coil_no: planning.coil_no || '', photocell_size: planning.photocell_size || '', photocell_colour: planning.photocell_colour || '',
    registration_marks_url: planning.registration_marks_url || '',
    registration_marks_position: planning.registration_marks_position || '',
    planning_special_instructions: planning.special_instructions || '',
    engraving_type: engraving.engraving_type || '', double_engraving: engraving.double_engraving || '',
    screen: engraving.screen?.toString() || '', gsm: engraving.gsm?.toString() || '',
    engraving_special_instructions: engraving.instruction || engraving.special_instructions || '',
    rework_history: engraving.rework_history || '',
    printing_substrate: proofing.printing_substrate || '',
    lamination_with: proofing.lamination_substrate || proofing.lamination_with || '',
    image_to_match_with: proofing.image || proofing.image_to_match_with || '',
    special_colours_to_match_with: proofing.special_colour || proofing.special_colours_to_match_with || '',
    special_colour_ink_provided: proofing.special_colour_ink_provided || '',
    proofing_to_be_laminated: proofing.proofing_to_be_laminated || '',
    proof_matching_reference: proofing.proof_matching_reference || '',
    colours: proofing.colours?.length > 0 ? proofing.colours : [''],
    proofing_instruction: proofing.instruction || '',
    proofing_remark: proofing.remark || '',
    consignee: billing.consignee || order.consignee || '',
    transporter: billing.transporter || order.transporter || '',
    dispatch_date: toDateInput(billing.dispatch_date || order.dispatch_date),
  };
};

// -------- 3) Form → API payload (for create/update) --------
const num = (v) => (v ? parseFloat(v) : null);
const int = (v) => (v ? parseInt(v, 10) : null);
const yes = (v) => v === 'Yes';

export const buildOrderPayload = (orderData, selectedCustomer) => ({
  customer_id: selectedCustomer,
  job_name: orderData.job_name,
  specifications: {
    width: num(orderData.width),
    height: num(orderData.height),
    ups_in_circum: int(orderData.ups_in_circum),
    ups_in_length: int(orderData.ups_in_length),
    circumference: parseFloat(orderData.circumference),
    length: parseFloat(orderData.length),
    nos_of_cylinders: parseInt(orderData.nos_of_cylinders, 10),
    bore: parseFloat(orderData.bore),
    degree: parseFloat(orderData.degree),
    trimming: num(orderData.trimming),
    extra_length: parseFloat(orderData.extra_length),
    actual_extra_length: num(orderData.actual_extra_length),
    key_way: orderData.key_way || null,
    new_baseshell: orderData.new_baseshell || null,
    pbs: orderData.pbs || null,
    manufactured_by: orderData.manufactured_by || null,
    marketed_by: orderData.marketed_by || null,
    brand_name: orderData.brand_name || null,
    product_name: orderData.product_name || null,
    qty: orderData.qty || null,
    packed_date: orderData.packed_date || null,
    bar_code: orderData.bar_code || null,
  },
  planning_specification: {
    registration_marks_url: orderData.registration_marks_url || null,
    registration_marks_position: orderData.registration_marks_position || null,
    special_instructions: orderData.planning_special_instructions || null,
    design_approved_date: orderData.design_approved_date || null,
    separations_approved_date: orderData.separations_approved_date || null,
    pcb: yes(orderData.pcb), veg_logo: yes(orderData.veg_logo), white: yes(orderData.white),
    overcoat: yes(orderData.overcoat), mattcoat: yes(orderData.mattcoat),
    barcode_genuine: yes(orderData.barcode_genuine), barcode_fake: yes(orderData.barcode_fake),
    repeat_trap: yes(orderData.repeat_trap), registration_marks: yes(orderData.registration_marks),
    slitting: yes(orderData.slitting), arc_marks: yes(orderData.arc_marks),
    gray_scale_circles: yes(orderData.gray_scale_circles), tt_line: yes(orderData.tt_line),
    special_colour_patches: yes(orderData.special_colour_patches),
    coil_no: orderData.coil_no || null,
    photocell_size: orderData.photocell_size || null,
    photocell_colour: orderData.photocell_colour || null,
  },
  engraving_specification: {
    engraving_type: orderData.engraving_type || null,
    double_engraving: orderData.double_engraving || null,
    screen: num(orderData.screen),
    gsm: num(orderData.gsm),
    special_instructions: orderData.engraving_special_instructions || null,
    instruction: orderData.engraving_special_instructions || null,
    rework_history: orderData.rework_history || null,
  },
  proofing_specification: {
    printing_substrate: orderData.printing_substrate || null,
    lamination_substrate: orderData.lamination_with || null,
    lamination_with: orderData.lamination_with || null,
    image_to_match_with: orderData.image_to_match_with || null,
    image: orderData.image_to_match_with || null,
    special_colours_to_match_with: orderData.special_colours_to_match_with || null,
    special_colour: orderData.special_colours_to_match_with || null,
    special_colour_ink_provided: orderData.special_colour_ink_provided || null,
    proofing_to_be_laminated: orderData.proofing_to_be_laminated || null,
    proof_matching_reference: orderData.proof_matching_reference || null,
    colours: (orderData.colours || []).filter(c => c && c.trim() !== ''),
    instruction: orderData.proofing_instruction || null,
    remark: orderData.proofing_remark || null,
  },
  biller: orderData.biller || null,
  consignee: orderData.consignee || null,
  transporter: orderData.transporter || null,
  pricing: num(orderData.pricing),
  delivery_date: orderData.delivery_date || null,
  dispatch_date: orderData.dispatch_date || null,
  design_approved_date: orderData.design_approved_date || null,
  separations_approved_date: orderData.separations_approved_date || null,
  notes: orderData.notes || null,
});
